import { createSelector } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import type { CompletePersonProfile } from '@appTypes/esb';
import { userDataSel } from '@modules/profile/duck/selectors';

/**
 * Returns the current logged in user date of birth as a string `yyyy-mm-dd`
 */
export const userDateOfBirthSel = createSelector(
  userDataSel,
  userData => userData.dateOfBirth,
);

/**
 * Returns the current logged in user age as a `number`
 * floored to the closest integer.
 */
export const currentUserAgeSel = createSelector(
  userDateOfBirthSel,
  dateOfBirth =>
    Math.floor(Math.abs(DateTime.fromISO(dateOfBirth).diffNow('years').years)),
);

/** Gets the MemberID for the current logged-in user. */
export const currentUserMIDSel = createSelector(
  userDataSel,
  userData => userData.memberId,
);
export const currentUserPersonGuidSel = createSelector(
  userDataSel,
  userData => userData.personGuid,
);

export const currentUserNamesSel = createSelector(userDataSel, userData => ({
  firstName: userData.firstName as string,
  lastName: userData.lastName as string,
}));

export const currentUserAddressesSel = createSelector(
  userDataSel,
  userData => userData.addresses as CompletePersonProfile['addresses'],
);

export const currentUserPrimaryAddressesSel = createSelector(
  currentUserAddressesSel,
  addresses =>
    addresses.find(
      address => address.isPrimary || address.isPrimary === 'TRUE',
    ),
);

export const currentUserPhonesSel = createSelector(
  userDataSel,
  userData => userData.phones as CompletePersonProfile['phones'],
);

export const currentUserPrimaryPhoneSel = createSelector(
  currentUserPhonesSel,
  phones => phones.find(phone => phone.isPrimary || phone.isPrimary === 'TRUE'),
);

export const currentUserEmailsSel = createSelector(
  userDataSel,
  userData => userData.emails as CompletePersonProfile['emails'],
);

export const currentUserPrimaryEmailSel = createSelector(
  currentUserEmailsSel,
  emails => emails.find(email => email.isPrimary || email.isPrimary === 'TRUE'),
);
